import { useTranslation } from 'react-i18next';
import { Link, Redirect } from 'react-router-dom';
import { Title } from '../../components/Title/Title';
import { Topbar } from '../../components/Topbar/Topbar';
import { Alert, Button, Container, Modal } from 'react-bootstrap';
import { useCurrentLanguage } from '../../../hooks/useCurrentLanguage';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useStripe } from '@stripe/react-stripe-js';
import { useCapacitor } from '../../../hooks/useCapacitor';
import { useEffect, useRef, useState } from 'react';
import { Browser } from '@capacitor/browser';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { Copyable } from '../../components/Copyable/Copyable';
import { canBuyEcm, getPriceInterval, formatSecondsTimestamp } from '../../utils/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import './BuyEcm.scss';
import { If, Then } from 'react-if';

let resumeFlowPromise = null;
let resumeFlowData = null;
let hasExecutedResumeFlow = false;
let isResumeFlowLoading = false;

const BuyEcmComponent = () => {
  const {
    plans,
    subscription,
    addCheckoutSession,
    promoCodes,
    userDataFmt,
    updateCustomerSubscription,
    getCustomerPortalUrl
  } = useFirebase();
  const { currentLanguage, currentLocale } = useCurrentLanguage();
  const stripe = useStripe();

  const { isNative } = useCapacitor();

  const [planUpgradeLoading, setPlanUpgradeLoading] = useState(false);
  const [isPlanUpgradeModalOpen, setIsPlanUpgradeModalOpen] = useState(false);
  const [planUpgradeData, setPlanUpgradeData] = useState<any>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorPaymentModalOpen, setIsErrorPaymentModalOpen] = useState(false);
  const [errorCase, setErrorCase] = useState('');

  const [loading, setLoading] = useState(false);
  const [visiblePromoCodes, setVisiblePromoCodes] = useState([]);
  const [visiblePlans, setVisiblePlans] = useState([]);

  const [portalUrlLoading, setPortalUrlLoading] = useState(false);
  const [showResumeFlowLoader, setShowResumeFlowLoader] = useState(false);

  const [checkoutSessionData, setCheckoutSessionData] = useState<{ sessionId: string; url: string }>(null);
  const [promoCodeReminderModal, setPromoCodeReminderModal] = useState(false);
  const skipFirstFalse = useRef(true);
  const { t } = useTranslation();

  // Clean up function for closing the modal
  const handleCloseModal = () => {
    setIsPlanUpgradeModalOpen(false);
    resumeFlowData = null;
    hasExecutedResumeFlow = false;
    isResumeFlowLoading = false;
    setShowResumeFlowLoader(false);
  };

  useEffect(() => {
    return () => {
      if (!isPlanUpgradeModalOpen) {
        resumeFlowData = null;
        hasExecutedResumeFlow = false;
      }
    };
  }, [isPlanUpgradeModalOpen]);

  // Checking URL for params ("resumeFlow" coming from Stripe customerPortal)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldResumeFlow = params.get('resumeFlow') === 'true';
    if ((shouldResumeFlow && subscription) || resumeFlowPromise || isResumeFlowLoading) {
      setShowResumeFlowLoader(true);
      isResumeFlowLoading = true; // Set loading flag
    }
  }, [subscription]);

  useEffect(() => {
    const checkResumeFlow = async () => {
      // Check if local variable has resumeFlowData
      if (resumeFlowData && !isPlanUpgradeModalOpen) {
        setPlanUpgradeData(resumeFlowData);
        setIsPlanUpgradeModalOpen(true);
        setShowResumeFlowLoader(false);
        isResumeFlowLoading = false;
        return;
      }

      // Check if local variable has a pending promise
      if (resumeFlowPromise) {
        try {
          const result = await resumeFlowPromise;
          if (result && !(result as any).error) {
            setPlanUpgradeData(result);
            setIsPlanUpgradeModalOpen(true);
            setShowResumeFlowLoader(false);
            isResumeFlowLoading = false;
          } else {
            // Error case - hide loader
            setShowResumeFlowLoader(false);
            isResumeFlowLoading = false;
          }
        } catch (e) {
          console.error('Error waiting for promise:', e);
          setShowResumeFlowLoader(false);
          isResumeFlowLoading = false;
        }
      }
    };

    checkResumeFlow();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldResumeFlow = params.get('resumeFlow') === 'true';

    if (shouldResumeFlow && subscription && !hasExecutedResumeFlow) {
      hasExecutedResumeFlow = true;
      isResumeFlowLoading = true; // Set loading flag
      setShowResumeFlowLoader(true); // Show loader

      // Remove params from URL
      params.delete('resumeFlow');
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, '', newUrl);

      // Create and store the promise
      resumeFlowPromise = updateCustomerSubscription(
        subscription.product.id,
        null,
        null,
        null,
        true,
        true
      ).then((pendingResult) => {

        if (!pendingResult) {
          resumeFlowPromise = null;
          isResumeFlowLoading = false;
          setShowResumeFlowLoader(false);
          return null;
        }

        if ((pendingResult as any).error) {
          console.error('Error in result:', pendingResult);
          resumeFlowPromise = null;
          isResumeFlowLoading = false;
          setShowResumeFlowLoader(false);
          setErrorCase((pendingResult as any).case);
          setIsErrorPaymentModalOpen(true);
          return null;
        }

        resumeFlowData = pendingResult;
        resumeFlowPromise = null;

        return pendingResult;
      }).catch((error) => {
        console.error('Promise rejected:', error);
        resumeFlowPromise = null;
        isResumeFlowLoading = false;
        setShowResumeFlowLoader(false);
        throw error;
      });
    }
  }, [subscription]);

  useEffect(() => {
    if (!userDataFmt) return;
    if (!promoCodes?.length) return;
    const myCategory = userDataFmt.profile?.formData?.categoriaProfessionale;
    if (!['odontoiatra'].includes(myCategory)) return;
    const _visiblePromoCodes = promoCodes.filter((promoCode) => promoCode.metadata?.CATEGORY == myCategory && promoCode.metadata?.ECM_INCLUDED == 'yes');
    setVisiblePromoCodes(_visiblePromoCodes);
  }, [promoCodes, userDataFmt]);

  useEffect(() => {
    if (!userDataFmt) return;
    if (!plans?.length) return;
    const myCategory = userDataFmt.profile?.formData?.categoriaProfessionale;
    if (!['odontoiatra'].includes(myCategory)) return;
    const _visiblePlans = plans.filter((plan) => plan.metadata?.CATEGORY == myCategory && plan.metadata?.ECM_INCLUDED == 'yes');
    setVisiblePlans(_visiblePlans);
  }, [plans, userDataFmt]);

  useEffect(() => {
    if (promoCodeReminderModal) return;
    if (skipFirstFalse.current) {
      skipFirstFalse.current = false;
      return;
    }
    if (!checkoutSessionData) return;
    runPayment(checkoutSessionData).catch(console.error);
  }, [promoCodeReminderModal]);

  const buy = async (priceId: string) => {
    setLoading(true);
    try {
      const { sessionId, url } = await addCheckoutSession(priceId, null, true);
      if (visiblePromoCodes.length) {
        setCheckoutSessionData({ sessionId, url });
        setPromoCodeReminderModal(true);
      } else await runPayment({ sessionId, url });
    } catch (e) {
      try {
        alert(JSON.stringify(e, null, 2));
      } catch {
        alert(e);
      }
    } finally {
      setLoading(false);
    }
  };

  const runPayment = async ({ sessionId, url }) => {
    if (isNative) {
      await Browser.open({ url });
    } else await stripe.redirectToCheckout({ sessionId });
  };

  const getActivePrice = (plan) => {
    const [activePrice] = plan.prices.filter((price) => price.active && !price.metadata?.UPGRADE);
    return activePrice;
  };

  if (!canBuyEcm(subscription, userDataFmt)) {
    return <Redirect to={'/browse'} />;
  }

  const listaCorsi: { title: string, description: string }[] = [
    {
      title: t('buyEcm.labels.corsoTitolo'),
      description: t('buyEcm.labels.corsoDescrizione')
    },
    {
      title: t('buyEcm.labels.corsoTitolo2'),
      description: t('buyEcm.labels.corsoDescrizione2')
    },
    {
      title: t('buyEcm.labels.corsoTitolo3'),
      description: t('buyEcm.labels.corsoDescrizione3')
    },
    {
      title: t('buyEcm.labels.corsoTitolo4'),
      description: t('buyEcm.labels.corsoDescrizione4')
    },
    {
      title: t('buyEcm.labels.corsoTitolo5'),
      description: t('buyEcm.labels.corsoDescrizione5')
    },
  ];

  return !showResumeFlowLoader && !!visiblePlans.length ? (
    <>
      <Title>{t('plans.labels.acquistaECM')}</Title>
      <div style={{ position: 'absolute', top: '0', left: '0', right: '0', zIndex: 100 }}>
        <Topbar />
      </div>

      <> {/*#region STEP 1  */}
        <div className={'text-center step-height'}>
          <div className={'background-top-side'} style={{ backgroundImage: `url(${process.env.PUBLIC_URL}one-to-one/elenco-appuntamenti.jpg)` }}></div>
          <Container>

            <h1 className={'font-weight-bold mb-5'}>{t('buyEcm.labels.tootorECM')}</h1>
            <h3 className={'font-weight-bold mb-4'}>{t('buyEcm.labels.benvenuto')}</h3>
            <h3 className={'mb-5'}>{t('buyEcm.labels.completa')} <br /> {t('buyEcm.labels.completa2')}</h3>

            <Button onClick={() => {
              document.getElementById('region-description').scrollIntoView({ behavior: 'smooth' });
            }} size='lg'>{t('buyEcm.labels.scopri')}</Button>
          </Container>
        </div>
      </>

      <>{/* #region STEP 2 */}
        <div className={'step-height container d-flex justify-content-center'} id='region-description'>
          <div className={'d-flex flex-column flex-md-row '}>
            <div className={'w-100 w-md-50 px-2'} style={{ backgroundImage: `url(${process.env.PUBLIC_URL}ecm/grafica-ecm.png)`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center' }}></div>
            <div className={"d-flex flex-column w-100 w-md-50 px-2"}>
              <h3 className={'mb-4 line-height'}>{t('buyEcm.labels.infoSottoscrivi')}
              </h3>
              <h3 className={'mb-5'}>{t('buyEcm.labels.infoSottoscrivi2')}</h3>
              <h2 className={'font-weight-bold'}>{t('buyEcm.labels.infoSottoscrivi3')}</h2>
            </div>
          </div>
          <div className={'text-center mt-5'}>
            <h2 className={'mb-4'}>{t('buyEcm.labels.costo')}</h2>
            <Button onClick={() => {
              document.getElementById('region-piani').scrollIntoView({ behavior: 'smooth' });
            }} size='lg'>{t('buyEcm.labels.scopri2')}</Button>
          </div>
        </div>
      </>

      <>{/* #region STEP 3 */}
        <div className={'step-height container d-flex justify-content-center'} id='region-piani'>
          <h1 className={'font-weight-bold mb-5'}>{t('buyEcm.labels.scegli')}</h1>
          <div className={'d-flex flex-column flex-md-row w-100 w-md-100 px-2 px-md-5'}>
            <div className={'card-prezzario px-3 py-2 border border-dark rounded d-flex flex-column align-items-center w-md-100 mb-2 mb-md-5'}>
              365,00 {t('buyEcm.labels.bloccoCosto')}
              <p>{t('buyEcm.labels.bloccoCosto2')}</p>
              {!!subscription ? <span className={'font-weight-bold'}> {t('buyEcm.labels.bloccoCostoAbbonato')}</span> : null}
            </div>
          </div>
          <div className={'d-flex flex-column flex-md-row '}>
            <div className={"d-flex flex-column w-100 w-md-50 px-2 px-md-5"}>
              <h2 className={'mb-4 text-center '}>{t('buyEcm.labels.utenteFree')}</h2>
              <div className={'feature-item mb-4'}>
                <img className={'w-md-10 feature-icon'} src={process.env.PUBLIC_URL + '/ecm/Tootor_ECM_Icona_ECM_1.png'} alt="Icona ECM" /><h4 className={'feature-text'}>{t('buyEcm.labels.benefitCommon')}</h4>
              </div>
              <div className={'feature-item mb-4'}>
                <img className={'w-md-10 feature-icon'} src={process.env.PUBLIC_URL + '/ecm/Tootor_ECM_Icona_ECM_2.png'} alt="Icona accesso illimitato" /><h4 className={'feature-text'}>{t('buyEcm.labels.benefitFree')}</h4>
              </div>
            </div>
            <div className={"d-flex flex-column w-100 w-md-50 mt-5 mt-md-0 px-2 px-md-5"}>
              <h2 className={'mb-4 text-center '}>{t('buyEcm.labels.utenteUpgrade')}</h2>
              <div className={'feature-item mb-4'}>
                <img className={'w-md-10 feature-icon'} src={process.env.PUBLIC_URL + '/ecm/Tootor_ECM_Icona_ECM_4.png'} alt="Icona pagamento parziale" /><h4 className={'feature-text'}>{t('buyEcm.labels.benefitUpgrade')}</h4>
              </div>
              <div className={'feature-item mb-4'}>
                <img className={'w-md-10 feature-icon'} src={process.env.PUBLIC_URL + '/ecm/Tootor_ECM_Icona_ECM_1.png'} alt="Icona ECM" /><h4 className={'feature-text'}>{t('buyEcm.labels.benefitCommon')}</h4>
              </div>
              <div className={'feature-item mb-4'}>
                <img className={'w-md-10 feature-icon'} src={process.env.PUBLIC_URL + '/ecm/Tootor_ECM_Icona_ECM_3.png'} alt="Icona continua ad usufruire" /><h4 className={'feature-text'}>{t('buyEcm.labels.benefitUpgrade2')}</h4>
              </div>
            </div>
          </div>
          <div className={'text-center mt-5'}>
            <Button onClick={() => {
              document.getElementById('region-corsi').scrollIntoView({ behavior: 'smooth' });
            }} className={'mb-5'} size='lg'>{t('buyEcm.labels.scopri3')}</Button>
          </div>
        </div>
      </>

      <> {/*#region STEP 4  */}
        <div className={'text-center step-height'} id="region-corsi">
          <Container className={'position-relative mt-auto'} style={{ zIndex: 10 }}>
            <div className={'courses-section'}>
              <h1 className={'font-weight-bold mb-5'}>{t('buyEcm.labels.corsiBenefit')}</h1>
              <div className={'course-list-container'}>
                {listaCorsi.map((corso) => (
                  <div className={'list-item border border-dark rounded'}>
                    <h3 style={{ height: '100px' }} className={"list-item-title w-md-25 w-100"}>{corso.title}</h3>
                    <h4 className={"list-item-description w-md-75 w-100"}>{corso.description}</h4>
                  </div>
                ))}
              </div>

              {!!visiblePromoCodes.length &&
                visiblePromoCodes.map((promoCode) => (
                  <Alert key={promoCode.id} variant={'success'} className={'mt-2 text-center'}>
                    {t('plans.labels.utilizzaCodice')}{' '}
                    <strong>
                      <Copyable text={promoCode.code} />
                    </strong>{' '}
                    {t('plans.labels.utilizzaCodice2', {
                      promoCode: (promoCode.coupon.amount_off / 100).toFixed(2),
                      interval: getPriceInterval(promoCode.metadata?.interval, true) ? t(`labels.${getPriceInterval(promoCode.metadata?.interval, true)}`) : '',
                    })}
                  </Alert>
                ))}

              <Button
                className={'mt-4'}
                size={'lg'}
                variant={'primary'}
                disabled={loading}
                onClick={() => {
                  if (planUpgradeLoading) return;
                  if (!subscription) {
                    setPlanUpgradeLoading(true);
                    buy(getActivePrice(visiblePlans[0]).id);
                    return;
                  } else {
                    setPlanUpgradeLoading(true);
                    updateCustomerSubscription(subscription.product.id, null, null, null, true, true)
                      .then((pendingResult) => {
                        if (!pendingResult) return;
                        if ((pendingResult as any).error) {
                          setErrorCase((pendingResult as any).case);
                          setIsErrorPaymentModalOpen(true);
                          return;
                        }
                        setPlanUpgradeData(pendingResult);
                        setIsPlanUpgradeModalOpen(true);
                      })
                      .catch(console.error)
                      .finally(() => {
                        setPlanUpgradeLoading(false);
                      });
                  }
                }}>
                {planUpgradeLoading ? <FontAwesomeIcon icon={faCircleNotch} spin className={'mr-2'} /> : null}
                {planUpgradeLoading ? t('labels.attendiDots') : t('buyEcm.labels.iscriviti')}</Button>
            </div>
          </Container>
          <div className={'background-bottom-side mb-0'} style={{ backgroundImage: `url(${process.env.PUBLIC_URL}one-to-one/categorie.jpg)` }}></div>
        </div>
      </>

      <Modal show={promoCodeReminderModal} onHide={() => setPromoCodeReminderModal(false)} centered backdrop={'static'} keyboard={false}>
        <Modal.Header>
          <Modal.Title>{t('plans.labels.ricordaCodiceSconto')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>{t('plans.labels.ricordaCodiceScontoDescrizione')}</div>
          {visiblePromoCodes.map((promoCode) => (
            <div key={promoCode.id}>
              &bull;{' '}
              <strong>
                <Copyable text={promoCode.code} />
              </strong>{' '}
              {t('plans.labels.sulPiano', { interval: t(`labels.${getPriceInterval(promoCode.metadata?.interval, true) || 'emptyLabel'}`) })}
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant={'primary'} onClick={() => setPromoCodeReminderModal(false)}>
            {t('labels.continua')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={isPlanUpgradeModalOpen} onHide={handleCloseModal} backdrop={'static'} keyboard={false}>
        <Modal.Header>
          <Modal.Title>{t('profile.labels.passaggioPianoAnnualeECM')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!!planUpgradeData && (
            <>
              <p>
                {t('profile.labels.staiPassandoAnnulaAlCostoECM')}{' '}
                <span className={'font-weight-bold'}>
                  &euro; {(planUpgradeData.proratedAmountDue / 100).toFixed(2)}
                </span>
                .
              </p>
              <Alert variant={'warning'}>
                {t('profile.labels.passaggioPianoDescrizioneECM')}<br /><br />
                {t('profile.labels.passaggioPianoDescrizioneECM2', { proratedAmount: (planUpgradeData.proratedAmountDue / 100).toFixed(2) })}
              </Alert>
              <Alert variant={'info'}>
                {t('profile.labels.passaggioPianoDescrizione1')}{' '}
                <span className={'font-weight-bold'}>{formatSecondsTimestamp(new Date().getTime() / 1000, undefined, currentLocale)}</span>.<br /><br />
                {t('profile.labels.passaggioPianoDescrizione4')}{' '}
              </Alert>
              <p>{t('profile.labels.passaggioPianoDescrizione3')}</p>
              <Alert variant={'danger'} className={'mt-2'}>
                {t('profile.labels.confermaPianoAnnualeECM')}<br /><br />
                {t('profile.labels.confermaPianoAnnualeECM2', { proratedAmount: (planUpgradeData.proratedAmountDue / 100).toFixed(2) })}<br />
              </Alert>
            </>
          )}
          <If condition={planUpgradeLoading}>
            <Then>
              <Alert variant={'warning'} className={'mt-2'}>
                {t('labels.attendiLong')}
              </Alert>
            </Then>
          </If>
        </Modal.Body>
        <Modal.Footer>
          <Button variant={'secondary'} disabled={planUpgradeLoading} onClick={() => setIsPlanUpgradeModalOpen(false)}>
            {t('labels.annulla')}
          </Button>
          <Button
            variant={'primary'}
            disabled={planUpgradeLoading}
            onClick={() => {
              setPlanUpgradeLoading(true);
              updateCustomerSubscription(!!planUpgradeData && planUpgradeData.stripeProductUpgradingTo.metadata.ECM_INCLUDED ? planUpgradeData.stripeProduct.id : null, null, null, null, true)
                .then((commitResult) => {
                  if (!commitResult) return;
                  setIsSuccessModalOpen(true);
                  setIsPlanUpgradeModalOpen(false);
                })
                .catch(console.error)
                .finally(() => {
                  setPlanUpgradeLoading(false);
                });
            }}
          >
            <FontAwesomeIcon icon={faCreditCard} fixedWidth={true} /> {planUpgradeLoading ? <>{t('labels.attendi')}&hellip;</> : t('labels.confermaECM') + ((planUpgradeData ? planUpgradeData.proratedAmountDue : 0) / 100).toFixed(2) + ' €'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal centered={isNative} show={isSuccessModalOpen} backdrop={'static'} onHide={() => { window.location.href = '/browse'; setIsSuccessModalOpen(false) }}>
        <Modal.Header>
          <Modal.Title>{t('plans.labels.planUpdateSuccessTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('plans.labels.planUpdateSuccess')}</Modal.Body>
        <Modal.Footer>
          <Button variant={'primary'} onClick={() => {
            setIsSuccessModalOpen(false);
            window.location.href = '/browse';
          }
          }>
            {t('labels.chiudi')}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal centered={isNative} show={isErrorPaymentModalOpen} backdrop={'static'} onHide={() => { setIsErrorPaymentModalOpen(false) }}>
        <Modal.Header>
          <Modal.Title>{t(errorCase === 'missing_card' ? 'buyEcm.labels.planUpdateErrorPaymentTitle' : 'buyEcm.labels.planUpdateErrorPaymentTitle2')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('buyEcm.labels.planUpdateErrorPayment')}</p>
          <p>{t('buyEcm.labels.planUpdateErrorPayment2')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant={'primary'}
            disabled={portalUrlLoading}
            onClick={() => {
              setPortalUrlLoading(true);
              getCustomerPortalUrl(true)
                .then(async (res) => {
                  if (isNative) {
                    await Browser.open({ url: res.url });
                  } else window.location.href = res.url;
                })
                .finally(() => setPortalUrlLoading(false));
            }}>
            {portalUrlLoading && <FontAwesomeIcon className={'mr-2'} icon={faCircleNotch} spin />}
            {portalUrlLoading ? <span>{t('labels.attendi')} &hellip;</span> : <span>{t('profile.labels.gestisciPiano')}</span>}
          </Button>
          <Button variant={'primary'} onClick={() => {
            setIsErrorPaymentModalOpen(false);
          }
          }>
            {t('labels.chiudi')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  ) : (<FullscreenLoader />);
};

export default BuyEcmComponent;
